import { forwardRef, useState } from "react";

import { AnimatePresence, motion } from "framer-motion";
import { Check, Copy } from "lucide-react";

import {
  IconButton,
  type IconButtonProps,
} from "@oneleet/components/src/atoms";

type Props = {
  content: string;
  /**
   * The amount of time in milliseconds to wait before setting the copied state to false.
   * Defaults to 2000 milliseconds.
   */
  delayMs?: number;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
} & Omit<IconButtonProps, "icon">;

const CopyButton = forwardRef<HTMLButtonElement, Props>(
  ({ content, delayMs = 2000, onClick, ...rest }, ref) => {
    const [isLoading, setIsLoading] = useState(false);
    const [copied, setCopied] = useState(false);

    const handleClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();

      try {
        setIsLoading(true);
        await navigator.clipboard.writeText(content);
        setCopied(true);
        setTimeout(() => {
          setCopied(false);
        }, delayMs);
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoading(false);
      }

      onClick?.(event);
    };

    return (
      <div className="relative inline-flex items-center">
        <AnimatePresence>
          {copied && (
            <motion.div
              initial={{ opacity: 0, x: -5 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -5 }}
              transition={{ duration: 0.3 }}
              className="absolute right-full mr-2 rounded-md bg-white px-2 py-1 font-sans text-gray-900"
            >
              Copied
            </motion.div>
          )}
        </AnimatePresence>
        <IconButton
          icon={copied ? <Check /> : <Copy />}
          onClick={handleClick}
          {...rest}
          ref={ref}
          isLoading={isLoading}
          color={copied ? "green" : rest.color}
        />
      </div>
    );
  },
);

CopyButton.displayName = "CopyButton";

export default CopyButton;
